import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shared } from '../../styles/shared.js';
import { LocaleController } from '../../i18n/locale-controller.js';
import { t } from '../../i18n/t.js';
import { generateSubtractionTrial, type SubtractionTrial } from '../../distractors/subtraction.js';
import { pickRandom } from '../../distractors/random.js';
import { getProgression } from '../../progression/context.js';
import { getAudioService } from '../../audio/context.js';
import { TRIALS_PER_SESSION, STREAK_THRESHOLD } from '../../session/config.js';
import type { SessionResult } from '../../progression/progression.js';
import { iconUrl, TRIAL_ICONS, type IconAsset } from '../../assets/icons.js';
import '../../components/digit-tile.js';
import '../../components/rewards-overlay.js';
import '../../components/streak-celebration.js';
import '../../components/session-summary.js';

type Phase = 'playing' | 'summary';

const FEEDBACK_DELAY_MS = 1000;
/** Beat to hold the demo-counted state after the last digit before advancing. */
const POST_AUDIO_HOLD_MS = 700;

@customElement('sub-game')
export class SubGame extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-5);
        height: 100%;
        padding: var(--space-5);
      }

      .prompt {
        font-family: var(--font-display);
        font-size: var(--type-l);
        text-align: center;
        margin: 0;
      }

      .objects {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
        justify-content: center;
        max-width: 920px;
      }

      .tiles {
        display: flex;
        gap: var(--space-5);
        justify-content: center;
        flex-wrap: wrap;
      }

      .progress {
        position: fixed;
        top: var(--space-3);
        right: var(--space-3);
        font-family: var(--font-display);
        font-weight: 700;
        font-size: var(--type-m);
        background: var(--color-surface);
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-pill);
        box-shadow: var(--shadow-tile);
      }

      @media (max-height: 500px) {
        :host {
          gap: var(--space-2);
          padding: var(--space-3);
        }

        .prompt {
          font-size: var(--type-m);
        }

        .objects {
          gap: var(--space-2);
        }

        .tiles {
          gap: var(--space-3);
        }

        .progress {
          font-size: var(--type-s);
          padding: var(--space-1) var(--space-3);
        }
      }
    `,
  ];

  @state() private phase: Phase = 'playing';
  @state() private trial: SubtractionTrial | null = null;
  @state() private trialIndex = 0;
  @state() private correctCount = 0;
  @state() private streak = 0;
  @state() private rewardShow = false;
  @state() private streakShow = false;
  @state() private locked = false;
  @state() private flashedTile: number | null = null;
  /** Position indices (within the A grid) currently shown as demo-counted. */
  @state() private demoCountedSet: ReadonlySet<number> = new Set();
  @state() private sessionResult: SessionResult | null = null;
  @state() private sessionIcon: IconAsset = TRIAL_ICONS[0]!;

  private locale = new LocaleController(this);

  override connectedCallback(): void {
    super.connectedCallback();
    this.sessionIcon = pickRandom(TRIAL_ICONS, Math.random);
    void this.preloadSessionAudio();
    this.startNextTrial();
  }

  private async preloadSessionAudio(): Promise<void> {
    const params = getProgression().subtractionParams();
    const keys = ['prompt.howmanyleft', 'feedback.correct', 'feedback.tryagain', 'feedback.zero'];
    for (let n = 0; n <= params.aMax; n++) keys.push(`digit.${n}`);
    await getAudioService().preload(keys);
  }

  private startNextTrial(): void {
    const params = getProgression().subtractionParams();
    this.trial = generateSubtractionTrial(params);
    this.locked = false;
    this.flashedTile = null;
    this.demoCountedSet = new Set();
    void getAudioService().play('prompt.howmanyleft', 'prompt');
  }

  private async onTileTap(value: number): Promise<void> {
    if (this.locked || this.trial === null) return;
    if (value === this.trial.diff) {
      await this.handleCorrect();
    } else {
      await this.handleWrong();
    }
  }

  private async handleCorrect(): Promise<void> {
    if (this.trial === null) return;
    this.locked = true;
    this.flashedTile = this.trial.diff;
    this.correctCount++;
    this.streak++;
    void getAudioService().play('feedback.correct', 'reward');
    this.rewardShow = true;
    if (this.streak >= STREAK_THRESHOLD) this.streakShow = true;
    const progression = getProgression();
    await progression.recordAttempt('subtraction', progression.getLevel('subtraction'), true);
    window.setTimeout(() => this.advance(), FEEDBACK_DELAY_MS);
  }

  private async handleWrong(): Promise<void> {
    if (this.trial === null) return;
    this.locked = true;
    this.streak = 0;
    const progression = getProgression();
    await progression.recordAttempt('subtraction', progression.getLevel('subtraction'), false);

    const audio = getAudioService();
    await audio.play('feedback.tryagain', 'reward');

    if (this.trial.diff === 0) {
      // Nothing to count — just announce zero.
      await audio.play('feedback.zero', 'reward');
    } else {
      // Walk through the non-removed positions in display order, highlighting
      // each as the corresponding digit plays.
      const removed = new Set(this.trial.removedIndices);
      const remaining: number[] = [];
      for (let pos = 0; pos < this.trial.a; pos++) {
        if (!removed.has(pos)) remaining.push(pos);
      }
      this.demoCountedSet = new Set();
      for (let i = 0; i < remaining.length; i++) {
        const next = new Set(this.demoCountedSet);
        next.add(remaining[i]!);
        this.demoCountedSet = next;
        await audio.play(`digit.${i + 1}`, 'reward');
      }
    }
    window.setTimeout(() => this.advance(), POST_AUDIO_HOLD_MS);
  }

  private advance(): void {
    this.trialIndex++;
    if (this.trialIndex >= TRIALS_PER_SESSION) {
      void this.finishSession();
      return;
    }
    this.startNextTrial();
  }

  private async finishSession(): Promise<void> {
    const result = await getProgression().recordSessionResult('subtraction', this.correctCount);
    this.sessionResult = result;
    this.phase = 'summary';
  }

  override render() {
    if (this.phase === 'summary' && this.sessionResult) {
      return html`<session-summary
        .starsEarned=${this.correctCount}
        .promoted=${this.sessionResult.promoted}
        .demoted=${this.sessionResult.demoted}
      ></session-summary>`;
    }
    if (this.trial === null) return html``;

    const removedSet = new Set(this.trial.removedIndices);
    const positions: number[] = [];
    for (let i = 0; i < this.trial.a; i++) positions.push(i);

    return html`<div class="progress" data-testid="progress">
        ${this.trialIndex + 1} / ${TRIALS_PER_SESSION}
      </div>
      <h2 class="prompt">${t(this.locale.locale, 'prompt.howmanyleft')}</h2>
      <div class="objects" data-testid="objects">
        ${positions.map((i) => {
          const isRemoved = removedSet.has(i);
          const isDemoCounted = !isRemoved && this.demoCountedSet.has(i);
          const cls =
            'count-obj' + (isRemoved ? ' removed' : '') + (isDemoCounted ? ' counted' : '');
          return html`<div
            class=${cls}
            data-testid="sub-obj"
            data-removed=${isRemoved ? 'true' : 'false'}
            data-demo-counted=${isDemoCounted ? 'true' : 'false'}
            aria-hidden=${isRemoved ? 'true' : 'false'}
          >
            <img src=${iconUrl(this.sessionIcon)} alt=${this.sessionIcon.labelEn} />
          </div>`;
        })}
      </div>
      <div class="tiles">
        ${this.trial.tiles.map(
          (v) =>
            html`<digit-tile
              variant="digit"
              .value=${v}
              ?disabled=${this.locked && this.flashedTile !== v}
              ?flash=${this.flashedTile === v}
              data-testid="sub-tile"
              data-correct=${v === this.trial?.diff ? 'true' : 'false'}
              @tile-tap=${(e: CustomEvent<{ value: number | null }>) =>
                this.onTileTap(e.detail.value!)}
            ></digit-tile>`,
        )}
      </div>
      <rewards-overlay
        .show=${this.rewardShow}
        @reward-done=${() => (this.rewardShow = false)}
      ></rewards-overlay>
      <streak-celebration
        .show=${this.streakShow}
        @streak-done=${() => (this.streakShow = false)}
      ></streak-celebration>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sub-game': SubGame;
  }
}
